import { FormattedStreamData, getShape, nullShapeFormatter, StreamDataColumn } from '@squaredup/data-streams';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { extent } from '@visx/vendor/d3-array';
import { ScaleLinear, ScaleTime } from '@visx/vendor/d3-scale';
import { area as d3Area, line as d3Line } from 'd3-shape';
import { formatTickAsColumn } from 'dashboard-engine/util/tickFormatter';
import { getColor } from 'dashboard-engine/visualisations/getColor';
import { NUM_Y_TICKS } from 'dashboard-engine/visualisations/getGridYValues';
import { useVisualizationTheme } from 'dashboard-engine/visualisations/hooks/useVisualizationTheme';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { linearRegression, linearRegressionLine } from 'simple-statistics';
import { useDrilldownContext } from 'ui/editor/dataStream/contexts/DrilldownContext';
import { DataStreamLineGraphConfig } from '../Config';
import { getOptimalDecimalPlaces } from '../DataStreamLineGraph';
import { StreamDataSeries, toSeries } from '../LineGraphToSeries';

// The number of points above which we disable animation for performance reasons
export const DISABLE_ANIMATION_THRESHOLD = 250;

export type LineGraphPoint = {
    id: string;
    lineId: string;
    color: string;
    timestamp: number;
    value: number;
    valueFormatted?: string;
    position: {
        x: number;
        y: number;
    };
};

export type LineGraphSlice = {
    id: string;
    x0: number;
    x: number;
    y0: number;
    y: number;
    width: number;
    points: LineGraphPoint[];
};

export type LineGraphLine = {
    id: string;
    index: number;
    color: string;
    drilldownUrl?: string;
    points: LineGraphPoint[];
};

export type LineGraphTrendLine = {
    id: string;
    color: string;
    x1: number;
    x2: number;
    y1: number;
    y2: number;
};

const getDate = (d: { timestamp: number }) => d.timestamp;

export const useLines = (
    data: FormattedStreamData,
    config: DataStreamLineGraphConfig,
    width: number,
    height: number,
    margin: {
        top: number;
        right: number;
        bottom: number;
        left: number;
    }
) => {
    const { isDrilldownEnabled } = useDrilldownContext();
    const lineGraphTheme = useVisualizationTheme('line');

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Separately memoize the data that doesn't change with the graph size
    const staticData = useMemo(() => {
        const { series: seriesData, valueColumn, unitLabel } = toSeries(data, config, { isDrilldownEnabled });

        const shape = valueColumn?.shapeName && getShape(valueColumn?.shapeName);

        const seriesNormalized = normalizeSeriesData(seriesData);

        const series = config.cumulative ? cumulativeSum(seriesNormalized) : seriesNormalized;

        const yScaleDomain = getYScaleDomain(
            series.flatMap((s) => s.points).map((d) => d.value),
            config
        );

        const getLineGraphColor = (datum: LineGraphLine) => getColor(config, lineGraphTheme, datum.id, datum.index);
        const formatTick = getTickFormatter(valueColumn, data, yScaleDomain.min, yScaleDomain.max);

        const seriesFormatted = formatSeriesValues(series, formatTick);

        return {
            seriesFormatted,
            yScaleDomain,
            unitLabel,
            shape,
            getLineGraphColor,
            formatTick
        };
    }, [config, data, isDrilldownEnabled, lineGraphTheme]);

    return useMemo(() => {
        const { seriesFormatted, yScaleDomain, unitLabel, shape, getLineGraphColor, formatTick } = staticData;

        // X axis scale
        const dateScale = scaleUtc({
            range: [margin.left, innerWidth + margin.left],
            domain: extent(
                seriesFormatted.flatMap((d) => d.points),
                getDate
            ) as [number, number] // have to cast here otherwise TypeScript complains it could be undefined
        });

        // Y axis scale
        const valueScale = scaleLinear({
            range: [innerHeight + margin.top, margin.top],
            domain: [yScaleDomain.min, yScaleDomain.max]
        });

        const lines: LineGraphLine[] = seriesFormatted.map((seriesItem) => ({
            ...seriesItem,
            color: getLineGraphColor(seriesItem),
            points: seriesItem.points.map((d) => ({
                ...d,
                id: `${seriesItem.id}-${d.id}`,
                position: {
                    x: dateScale(d.timestamp) ?? 0,
                    y: valueScale(d.value) ?? 0
                }
            }))
        }));

        const trendLines = config.showTrendLine ? getTrendLines(lines, dateScale, valueScale) : undefined;

        // All line points flattened into a single array to be rendered together
        const points: LineGraphPoint[] = lines.flatMap((line, i) =>
            line.points.map((d) => ({
                id: `${line.id}-${i}-${d.id}`,
                lineId: line.id,
                position: d.position,
                timestamp: d.timestamp,
                value: d.value,
                valueFormatted: d.valueFormatted,
                color: line.color
            }))
        );

        // Slices are used to group points that share the same x position so we can display them in the tooltip
        const slices = getSlices(points, innerWidth);

        // The line and area generators are used to create the SVG paths for the lines and areas
        const lineGenerator = d3Line<{ x: number; y: number }>()
            .x((d) => d.x)
            .y((d) => d.y);

        const areaGenerator = d3Area<{ x: number; y: number }>()
            .x((d) => d.x)
            .y0(() => innerHeight + margin.top)
            .y1((d) => d.y);

        return {
            lines,
            trendLines,
            dateScale,
            valueScale,
            points,
            slices,
            lineGenerator,
            areaGenerator,
            yScaleDomain,
            unitLabel,
            shape,
            formatTick
        };
    }, [staticData, config.showTrendLine, innerHeight, innerWidth, margin.left, margin.top]);
};

/**
 * Generates an array of slices, which group line graph points by their x position
 */
const getSlices = (points: LineGraphPoint[], width: number): LineGraphSlice[] => {
    const map = new Map<number, LineGraphPoint[]>();

    // Group points by X position
    points.forEach((point) => {
        if (point.position.x === null || point.position.y === null) {
            return;
        }
        if (!map.has(point.position.x)) {
            map.set(point.position.x, [point]);
        } else {
            map.get(point.position.x)?.push(point);
        }
    });

    return Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([x, slicePoints], i, slices) => {
            const prevSlice = slices[i - 1];
            const nextSlice = slices[i + 1];

            let x0;
            if (!prevSlice) {
                x0 = x;
            } else {
                x0 = x - (x - prevSlice[0]) / 2;
            }

            let sliceWidth;
            if (!nextSlice) {
                sliceWidth = width - x0;
            } else {
                sliceWidth = x - x0 + (nextSlice[0] - x) / 2;
            }

            return {
                id: `slice:${x}`,
                x0,
                x,
                y0: 0,
                y: 0,
                width: sliceWidth,
                points: slicePoints.reverse()
            };
        });
};

/**
 * Creates a tick formatter function for the Y axis based on the value column and data.
 * The optimal number of decimal places is calculated based on the min/max value in the data.
 */
const getTickFormatter = (
    valueColumn: StreamDataColumn | undefined,
    data: FormattedStreamData,
    min: number,
    max: number
) => {
    if (valueColumn == null) {
        return nullShapeFormatter;
    }

    const formatter = formatTickAsColumn(valueColumn, { data, valueColumn });

    const optimalDecimalPlaces = getOptimalDecimalPlaces(
        min,
        max,
        NUM_Y_TICKS,
        valueColumn.rawShapeConfig.decimalPlaces
    );

    return (v: unknown) => {
        if (typeof v !== 'number') {
            return formatter(v);
        }
        return formatter(v, {
            decimalPlaces: optimalDecimalPlaces
        });
    };
};

/**
 * Converts series data into format usable for the line graph. Line points are ordered by timestamp.
 */
export const normalizeSeriesData = (seriesData: StreamDataSeries[] | undefined) =>
    seriesData?.map((s, idx) => ({
        id: s.label,
        index: idx,
        color: 'white',
        drilldownUrl: s.drilldownUrl,
        points: orderBy(
            s.points.map((d, i) => ({
                id: `${s.label}-${i}`,
                lineId: s.label,
                color: 'white',
                position: {
                    x: 0,
                    y: 0
                },
                timestamp: d.timestamp.getTime(),
                value: d.value as number,
                valueFormatted: d.valueFormatted
            })),
            'timestamp'
        )
    })) ?? [];

/**
 * Applies cumulative sum calculation on each point in the line.
 */
const cumulativeSum = (series: LineGraphLine[]) =>
    series.map((s) => ({
        ...s,
        points: s.points.map(
            ((sum: number) => (d) => {
                sum += d.value;
                return {
                    ...d,
                    value: sum
                };
            })(0)
        )
    }));

/**
 * Applies value formatting to each point in the series.
 */
export const formatSeriesValues = (series: LineGraphLine[], formatTick: (value: unknown) => string): LineGraphLine[] =>
    series.map((s) => ({
        ...s,
        points: s.points.map((d) => ({
            ...d,
            valueFormatted: formatTick(d.value)
        }))
    }));

/**
 * Calculates trend lines for each series in the line graph.
 * Uses linear regression to find the slope and intercept, then calculates the Y values for the min
 */
const getTrendLines = (
    series: LineGraphLine[],
    dateScale: ScaleTime<number, number, never>,
    valueScale: ScaleLinear<number, number, never>
): LineGraphTrendLine[] => {
    const allXValues = series.flatMap((d) => d.points.map((s) => s.timestamp));

    const xSeriesMin = Math.min(...allXValues);
    const xSeriesMax = Math.max(...allXValues);

    return series.map(({ id, color, points }) => {
        const coords = points.map((d) => [d.timestamp, d.value]);

        const { m, b } = linearRegression(coords);

        const minY = linearRegressionLine({ m, b })(xSeriesMin);
        const maxY = linearRegressionLine({ m, b })(xSeriesMax);

        return {
            id,
            color,
            x1: dateScale(xSeriesMin),
            x2: dateScale(xSeriesMax),
            y1: valueScale(minY),
            y2: valueScale(maxY)
        };
    });
};

/**
 * Calculates the min and max values for the Y scale based on all the values in a graph
 */
export const getYScaleDomain = (
    values: number[],
    config?: DataStreamLineGraphConfig
): {
    min: number;
    max: number;
} => {
    const maxY = Math.max(...values);
    const minY = Math.min(...values);

    const yAxisRangeFrom = !config?.yAxisRangeFrom || isNaN(config?.yAxisRangeFrom) ? null : config?.yAxisRangeFrom;
    const yAxisRangeTo = !config?.yAxisRangeTo || isNaN(config?.yAxisRangeTo) ? null : config?.yAxisRangeTo;

    switch (config?.yAxisRangeMode) {
        case 'percentage': {
            return { min: 0, max: 100 };
        }
        case 'fit': {
            return { min: 0, max: maxY };
        }
        case 'custom': {
            return {
                min: yAxisRangeFrom ?? minY,
                max: yAxisRangeTo ?? maxY
            };
        }
        default: {
            return { min: minY, max: maxY };
        }
    }
};
